/**
 * LadiesSellerOnboardingModal - Premium Seller Onboarding for Ladies Hub
 * Bilingual welcome modal with trust-focused positioning
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Shield, Star, Users, Sparkles } from 'lucide-react';
import { LadiesHubColors, LadiesHubRadii, LadiesHubMotion } from '../hubs/ladies/LadiesHubPremiumConfig';

const colors = LadiesHubColors;

const ONBOARDING_COPY = {
  en: {
    headline: "Welcome to Ladies Hub",
    tagline: "Curated for Women. Trusted Sellers. Quality Products.",
    intro: "You're about to post in our premium women-focused marketplace. Here's what makes Ladies Hub special:",
    features: [
      { icon: Shield, text: "All listings are reviewed for safety and authenticity" },
      { icon: CheckCircle, text: "Verified sellers get priority visibility" },
      { icon: Star, text: "Quality standards ensure trust" },
      { icon: Users, text: "A community built for women, by women" }
    ],
    cta: "Start Posting",
    footer: "Your listing will be visible to thousands of women looking for quality products and services."
  },
  ar: {
    headline: "مرحباً بكِ في قسم السيدات",
    tagline: "مختار للسيدات. بائعون موثوقون. منتجات أصلية.",
    intro: "أنتِ على وشك النشر في سوقنا الحصري للسيدات. إليكِ ما يميز قسم السيدات:",
    features: [
      { icon: Shield, text: "جميع الإعلانات تخضع لمراجعة للتأكد من السلامة والأصالة" },
      { icon: CheckCircle, text: "البائعات الموثقات يحصلن على أولوية في الظهور" },
      { icon: Star, text: "معايير الجودة تضمن الثقة" },
      { icon: Users, text: "مجتمع مبني للسيدات، من السيدات" }
    ],
    cta: "ابدأي النشر",
    footer: "سيظهر إعلانك لآلاف السيدات الباحثات عن منتجات وخدمات عالية الجودة."
  }
};

export default function LadiesSellerOnboardingModal({ isOpen, onClose, onContinue, lang = 'en' }) {
  const copy = ONBOARDING_COPY[lang] || ONBOARDING_COPY.en;
  const isRTL = lang === 'ar';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="ladies-onboarding-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 1000,
            }}
          />
          
          {/* Modal */}
          <motion.div
            className="ladies-onboarding-modal"
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 40 }}
            transition={LadiesHubMotion.springSoft}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'calc(100% - 32px)',
              maxWidth: 420,
              maxHeight: '90vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: LadiesHubRadii.sheet,
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.25)',
              zIndex: 1001,
              direction: isRTL ? 'rtl' : 'ltr',
            }}
            data-testid="ladies-seller-onboarding-modal"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: 16,
                right: isRTL ? 'auto' : 16,
                left: isRTL ? 16 : 'auto',
                background: 'rgba(0,0,0,0.05)',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              aria-label="Close"
              data-testid="onboarding-close-btn"
            >
              <X size={18} color="#666" />
            </button>
            
            {/* Content */}
            <div style={{ padding: '32px 24px 24px' }}>
              {/* Icon & Headline */}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.primary2})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)',
                  }}
                >
                  <Sparkles size={28} color="#fff" />
                </div>
                <h2 style={{ 
                  margin: '0 0 8px', 
                  fontSize: 22, 
                  fontWeight: 700, 
                  color: colors.ink 
                }}>
                  {copy.headline}
                </h2>
                <p style={{ 
                  margin: 0, 
                  fontSize: 14, 
                  color: colors.muted,
                  fontWeight: 500 
                }}>
                  {copy.tagline}
                </p>
              </div>
              
              {/* Intro */}
              <p style={{ 
                margin: '0 0 20px', 
                fontSize: 14, 
                color: colors.ink, 
                lineHeight: 1.6,
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {copy.intro}
              </p>
              
              {/* Features */}
              <div style={{ marginBottom: 24 }}>
                {copy.features.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + idx * 0.08 }}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background: 'rgba(245, 158, 11, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <feature.icon size={14} color={colors.primary} />
                    </div>
                    <span style={{ 
                      fontSize: 13, 
                      color: colors.ink, 
                      lineHeight: 1.5,
                      paddingTop: 4 
                    }}>
                      {feature.text}
                    </span>
                  </motion.div>
                ))}
              </div>
              
              {/* CTA Button */}
              <motion.button
                onClick={onContinue}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.primary2})`,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: 12,
                  cursor: 'pointer',
                  boxShadow: '0 6px 20px rgba(245, 158, 11, 0.35)',
                }}
                data-testid="onboarding-continue-btn"
              >
                {copy.cta}
              </motion.button>
              
              {/* Footer */}
              <p style={{ 
                margin: '16px 0 0', 
                fontSize: 12, 
                color: colors.muted, 
                textAlign: 'center',
                lineHeight: 1.5 
              }}>
                {copy.footer}
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
